import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../api/authApi';

type PageState = 'form' | 'loading' | 'success' | 'error';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [pageState, setPageState] = useState<PageState>('form');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setPageState('error');
      return;
    }

    setPageState('loading');
    setErrorMessage('');

    try {
      await requestPasswordReset({ email: email.trim() });
      setPageState('success');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send reset email');
      setPageState('error');
    }
  };

  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-success-600 dark:text-success-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Check Your Email
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            If an account exists for{' '}
            <strong className="text-primary-700 dark:text-primary-400">{email}</strong>, we've sent
            password reset instructions to that address.
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
            The link will expire in 1 hour. If you don't see the email, check your spam folder.
          </p>
          <Link
            to="/login"
            className="inline-block w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-colors text-center"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
      <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Forgot Password
          </h1>
          <p className="text-gray-600 dark:text-slate-400">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (pageState === 'error') {
                  setPageState('form');
                  setErrorMessage('');
                }
              }}
              placeholder="you@example.com"
              className="w-full px-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-primary-200 dark:border-primary-800 rounded-xl placeholder-gray-400 dark:placeholder-slate-500 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={pageState === 'loading'}
              autoComplete="email"
              autoFocus
            />
          </div>

          {pageState === 'error' && errorMessage && (
            <div className="p-4 bg-error-50/80 dark:bg-error-900/30 backdrop-blur-sm border border-error-200/50 dark:border-error-800/50 rounded-xl">
              <p className="text-sm text-error-600 dark:text-error-400">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={pageState === 'loading'}
            className="w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {pageState === 'loading' ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
